import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, effect, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { AppIconComponent } from '../app-icon/app-icon.component';
import { CommandItem, CommandPaletteService } from '../../services/command-palette.service';

/**
 * <app-command-palette>
 *
 * Overlay flotante con search + lista agrupada. Se monta UNA sola vez en
 * `app.component.html` y vive en el root del DOM — su z-index queda arriba
 * del sidebar/header sin pelear con stacking contexts.
 *
 * El estado vive en `CommandPaletteService` (signal-based). Este componente
 * solo:
 *   - Renderiza la UI cuando `service.isOpen()` es true.
 *   - Conecta el input al `service.setQuery()`.
 *   - Mapea ↑↓ Enter Escape a `service.moveSelection()` y `service.execute()`.
 *   - Auto-focus al input al abrir (con setTimeout para esperar el render).
 *
 * Animaciones:
 *   - Backdrop fade.
 *   - Sheet slide-up + scale subtle (estilo Linear).
 */
@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [CommonModule, TranslateModule, AppIconComponent],
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('overlayFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('180ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('140ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('sheetIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(16px) scale(0.97)' }),
        animate('260ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition(':leave', [
        animate('140ms ease-in',
          style({ opacity: 0, transform: 'translateY(8px) scale(0.98)' })),
      ]),
    ]),
  ],
})
export class CommandPaletteComponent implements AfterViewInit {
  service = inject(CommandPaletteService);

  inputEl = viewChild<ElementRef<HTMLInputElement>>('paletteInput');
  resultsEl = viewChild<ElementRef<HTMLDivElement>>('paletteResults');

  /** Total de resultados — usado para mostrar empty state. */
  totalResults = computed(() => this.service.results().total);

  /** Index global (en el flat results) del item bajo el selected pointer. */
  selectedIdx = computed(() => this.service.selectedIdx());

  constructor() {
    // Cuando se abre el palette, hacer focus al input despues del render.
    // Usamos effect (signal-aware) para reaccionar a isOpen.
    effect(() => {
      if (this.service.isOpen()) {
        // El template renderiza recien despues del cambio del signal —
        // necesitamos esperar 1 frame para que el input exista en el DOM.
        setTimeout(() => this.inputEl()?.nativeElement.focus(), 30);
      }
    });

    // Scroll into view del item selected cuando se navega con teclado, para
    // que nunca quede oculto debajo del fold del scroll del palette.
    effect(() => {
      const idx = this.selectedIdx();
      if (!this.service.isOpen()) return;
      // Esperar render — el class `.is-selected` se aplica en el siguiente tick.
      requestAnimationFrame(() => {
        const root = this.resultsEl()?.nativeElement;
        if (!root) return;
        const selected = root.querySelector('.cp-result.is-selected') as HTMLElement | null;
        selected?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        // Suprimimos el warning de "el signal idx no se uso" — el deps
        // tracker ya lo registro por el `.selectedIdx()` arriba.
        void idx;
      });
    });
  }

  ngAfterViewInit(): void {
    // No hace falta nada — el listener global Ctrl/Cmd+K vive en el service.
  }

  // ===== Keyboard handlers (solo activos cuando el palette esta abierto) =====
  // El listener global del service maneja ⌘K / Escape. Los movimientos ↑↓ y
  // Enter se manejan acá porque solo aplican cuando el input tiene foco.
  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(e: KeyboardEvent) {
    if (!this.service.isOpen()) return;
    const key = e.key;
    if (key === 'ArrowDown') {
      e.preventDefault();
      this.service.moveSelection(+1);
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      this.service.moveSelection(-1);
    } else if (key === 'Enter') {
      e.preventDefault();
      this.service.execute();
    }
    // Escape ya lo maneja el listener global del service.
  }

  onInput(value: string): void {
    this.service.setQuery(value);
  }

  onClickItem(item: CommandItem): void {
    this.service.execute(item);
  }

  onHoverItem(idx: number): void {
    this.service.selectedIdx.set(idx);
  }

  onBackdropClick(): void {
    this.service.close();
  }

  // ===== Helpers para el template =====

  /** Index global del item en el flat results. Usado para marcar `.is-selected`. */
  globalIdx(group: 'recents' | 'pages' | 'actions', localIdx: number): number {
    const r = this.service.results();
    if (group === 'recents') return localIdx;
    if (group === 'pages')   return r.recents.length + localIdx;
    return r.recents.length + r.pages.length + localIdx;
  }

  trackId = (_: number, item: CommandItem) => item.id;
}
